import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useMobil } from '../../lib/use-mobil';
import { IconButton } from './button';

/** Ab so vielen Pixeln nach unten gezogen schließt das Sheet. */
const SCHLIESSEN_AB = 80;

/** Was am Rechner als „erstes Feld“ den Fokus bekommt, in DOM-Reihenfolge. */
const BEDIENBAR = 'input:not([type=hidden]), textarea, select, button, a[href], [tabindex]:not([tabindex="-1"])';

/** Das erste sichtbare, bedienbare Element im Inhalt (ohne Kopf und Fuß). */
function erstesFeld(inhalt: HTMLElement | null): HTMLElement | null {
  if (!inhalt) return null;
  for (const el of inhalt.querySelectorAll<HTMLElement>(BEDIENBAR)) {
    if (!el.matches(':disabled') && el.getClientRects().length > 0) return el;
  }
  return null;
}

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Ein Satz unter dem Titel, z. B. der vorbelegte Kontext: „Zu Hausverwaltung Elbufer GmbH“. */
  description?: ReactNode;
  children?: ReactNode;
  /** Aktionen. Kleben unten, mit Abstand zur Home-Leiste; am Handy untereinander in voller Breite. */
  footer?: ReactNode;
}

/**
 * Fläche von unten fürs Handy: volle Breite, oben gerundet, Griff zum
 * Herunterziehen, höchstens 90 % der Höhe, Inhalt scrollt. Ab 768 px ein
 * normaler Dialog in der Mitte, damit dieselben Formulare am Rechner laufen.
 * (Ergänzung, README „Mobil“.)
 */
export function Sheet({ open, onOpenChange, title, description, children, footer }: SheetProps) {
  const mobil = useMobil();
  const [zug, setZug] = useState(0);
  const start = useRef<number | null>(null);
  const inhalt = useRef<HTMLDivElement>(null);
  // Ohne DialogTrigger (geöffnet aus einem Menü, dem Sidebar-Fuß, „Mehr“) wüsste
  // Radix nicht, wohin der Fokus beim Schließen soll, und ließe ihn auf <body>.
  const vorher = useRef<HTMLElement | null>(null);

  // Herunterziehen am Griff oder Kopf. Der Zeiger wird erst ab ein paar Pixeln
  // Bewegung gefangen, sonst landete der Tipp auf „Schließen“ nicht beim Knopf.
  const ziehen = mobil
    ? {
        onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
          if (e.button === 0) start.current = e.clientY;
        },
        onPointerMove: (e: PointerEvent<HTMLDivElement>) => {
          if (start.current == null) return;
          const dy = e.clientY - start.current;
          if (dy > 4 && !e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.setPointerCapture(e.pointerId);
          setZug(Math.max(0, dy));
        },
        onPointerUp: () => {
          if (zug > SCHLIESSEN_AB) onOpenChange(false);
          start.current = null;
          setZug(0);
        },
        onPointerCancel: () => {
          start.current = null;
          setZug(0);
        },
      }
    : {};

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[2000] bg-background/70 animate-ek-in" />
        <DialogPrimitive.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          // Am Handy Fokus auf das Sheet selbst, nicht auf das erste Feld: Sonst
          // ginge sofort die Tastatur auf, und „Schließen“ trüge einen Fokusring.
          // Am Rechner wie ein Dialog aufs erste Feld des Inhalts; Radix nähme
          // von selbst „Schließen“, das im Kopf vor den Feldern steht.
          onCloseAutoFocus={(e) => {
            // Zurück dorthin, wo man war — wenn es das Element noch gibt (aus „Mehr“
            // geöffnet ist es weg, dann entscheidet Radix).
            const ziel = vorher.current;
            vorher.current = null;
            if (ziel?.isConnected && ziel !== document.body) {
              e.preventDefault();
              ziel.focus();
            }
          }}
          onOpenAutoFocus={(e) => {
            // Merken, bevor der Fokus ins Sheet wandert.
            vorher.current = document.activeElement as HTMLElement | null;
            e.preventDefault();
            (erstesFeld(mobil ? null : inhalt.current) ?? (e.currentTarget as HTMLElement)).focus();
          }}
          style={zug > 0 ? { transform: `translateY(${zug}px)`, transition: 'none' } : undefined}
          className={cn(
            'fixed inset-x-0 bottom-0 z-[2000] flex max-h-[90dvh] flex-col rounded-t-xl border-t border-border bg-popover text-popover-foreground shadow-md animate-ek-sheet focus-visible:outline-none',
            'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]',
            'md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:max-h-[calc(100dvh-2rem)] md:w-[calc(100%-2rem)] md:max-w-lg md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-xl md:border md:animate-ek-pop',
          )}
        >
          <div {...ziehen} className="shrink-0 touch-none select-none md:touch-auto md:select-auto">
            <div aria-hidden className="mx-auto mt-2 h-1 w-10 rounded-full bg-input md:hidden" />
            <div className="flex items-start gap-3 px-5 pb-3 pt-3 md:px-6 md:pt-6">
              <div className="min-w-0 flex-1">
                <DialogPrimitive.Title className="t-title">{title}</DialogPrimitive.Title>
                {description && <DialogPrimitive.Description className="mt-0.5 t-small text-muted-foreground">{description}</DialogPrimitive.Description>}
              </div>
              <DialogPrimitive.Close asChild>
                <IconButton icon={X} label="Schließen" variant="ghost" size="sm" noTooltip className="-mr-1.5 -mt-0.5" />
              </DialogPrimitive.Close>
            </div>
          </div>
          {/*
            `data-sheet` benennt die Zusage des Sheets: Der Körper scrollt, der
            Fuß bleibt stehen. Wer etwas unterbringt, das nach einer Aktion
            gesehen werden muss, gehört in den Fuß — und kann das prüfen, ohne
            Klassennamen von hier abzulesen (Zonen-Panel, Befund B-49).
          */}
          <div
            ref={inhalt}
            data-sheet="koerper"
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 md:px-6',
              footer ? 'pb-4' : 'pb-[calc(1.25rem+env(safe-area-inset-bottom))] md:pb-6',
            )}
          >
            {children}
          </div>
          {footer && (
            <div
              data-sheet="fuss"
              className="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:justify-end md:border-t-0 md:px-6 md:pb-6 md:pt-2"
            >
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
